import { useDispatch } from "react-redux";
import { resetRatesBasedOnRatesType, setGCLIRates6680FromApi, setGYRTAndGPARates6680FromApi, setRateForAll, setRateGroups, setRates1865, setRidersLegend, setShowRates } from "../../features/actuarial/rates-slice";
import { PRODUCT_TYPE_IDS, PROPOSAL_RIDERS, RATES_CONSTANTS } from "../../constant/gms-constant";
import { setAnnualPremiumAmount, setEvidenceNotes } from "../../features/actuarial/annual-premium-eoi.slice";
import { defaultRateOf1865Dynamic } from "../../data/actuarial";
import { clearFilesDocuments, populateFilesDocuments } from "../../features/actuarial/document-slice";
import { setIsEditingNotesForProposal, setIsShowToPDF, setNoteForProposalAct, setNotes } from "../../features/actuarial/remark-slice";
import { ProposalData, Rate1865, AgeRangeKey, AgeRate, DynamicRate, Rate1865AgeKey, Rate6670, Rate7175, Rate7680, RiderRate } from "../../api/gms/proposal/proposal-api-state";
import { RateGroup, RateItem, RatesInfo, RiderLegend, RiderMeta } from "../../features/actuarial/rates-state";
import { useCallback } from "react";

export const usePopulateRates = (proposal?: ProposalData) => {
    
    const dispatch = useDispatch();

    const getAgeKey = useCallback((title: string) => {
        const rangeMap: Record<string, string> = {
            "18 to 24": "age_18_24",
            "25 to 29": "age_25_29",
            "30 to 34": "age_30_34",
        };
        if (rangeMap[title]) return rangeMap[title];
        return `age_${title}`;
    }, []);

    const populateRates = useCallback(() => {
        if(!proposal) return;
        const basePlanName: string = proposal.plan.name;
        const basePlanAcronym: string = proposal.plan.acronym;
        const basePlan: string = proposal.basic_plan.name;
        const basePlanId: number = proposal.basic_plan.id;
        const isGYRT: boolean = proposal.plan?.id === PRODUCT_TYPE_IDS.GYRT;
        const isGPA: boolean = proposal.plan?.id === PRODUCT_TYPE_IDS.GPA;
        const isGCLI: boolean = proposal.plan?.id === PRODUCT_TYPE_IDS.GCLI;
        const isGCI: boolean = proposal.plan?.id === PRODUCT_TYPE_IDS.GCI;
        const isGYRTOrIsGPAOrIsGCI: boolean = isGYRT || isGPA || isGCI;
        const numberOfLives: number = proposal.number_of_lives;
        const numberOfPaymentTerm: number = proposal?.payment?.[0]?.sub_payment_term.id ?? 0;

        const basicPlanData: RiderLegend = {
            acronym: basePlanAcronym,
            id: basePlanId,
            name: basePlanName
        }

        const riders: RiderLegend[] = proposal.riders?.map((rider) => ({
            id: rider.rider_id,
            name: rider.rider_name,
            acronym: rider.acronym ?? '',
        })) ?? [];

        const finalLegend: RiderLegend[] = [
            ...riders,
            basicPlanData
        ]
        dispatch(setRidersLegend(finalLegend))

        if(isGCLI) {
            // Convert proposal riders into the format used by the rates table
            const allRiders = proposal.riders ?? [];
            const normalizedRiders: RatesInfo[] = allRiders.map((rider) => ({
                type: "rider" as const,
                rider_id: rider.rider_id,
                plan_id: null,
                name: rider.rider_name,
                amount: String(rider.amount) ?? "",
                acronym: rider.acronym ?? "",
            }));
            dispatch(setRates1865(normalizedRiders)); 

            // Get 18-65 payment term rates
            const basicPlanGroup = proposal?.rates?.["18-65"] as Rate1865 | undefined;
            const basicPlan: AgeRate[] = basicPlanGroup?.basic_plan ?? [];
            // Create lookup of rider_id -> rider information
            const riderMetaMap: Map<number, RiderMeta> = new Map(
                (proposal.riders ?? []).map((r: any) => [
                    Number(r.rider_id),
                    r
                ])
            );
            // Base plan template
            const basePlanItem: RatesInfo = {
                type: 'plan',
                rider_id: null,
                plan_id: basicPlanGroup?.basic_plan_id ?? basePlanId,
                name: basePlan,
                amount: basicPlan[0]?.basic_rate ?? "",
                acronym: basePlanAcronym ?? "",
            }
            // Create rider template
            const reassembledRidesItem: RateItem[] = [
                basePlanItem,
                ...(proposal.riders?.map((rider) => {
                    return {
                        plan_id: null,
                        rider_id: rider.rider_id,
                        name: rider.rider_name,
                        amount: "", // Filled per payment term below
                        acronym: rider.acronym ?? "",
                        type: "rider" as const,
                    };
                }) ?? [])
            ];

            // Build rates for each payment term
            const rates: RateGroup[] = Array.from(
                { length: numberOfPaymentTerm },
                (_, index) => {
                    const month = basicPlan[index];
                    return {
                        title: `${index + 1} month${index + 1 > 1 ? "s" : ""}`,
                        data: reassembledRidesItem.map((item) => {
                            // Set base plan rate
                            if (item.type === "plan") {
                                return {
                                    ...item,
                                    amount: month?.basic_rate ?? "",
                                };
                            }
                            // Set rider rate for the current payment term
                            const monthRider = month?.riders?.find(
                                (r: any) =>
                                    Number(r.rider_id) === Number(item.rider_id)
                            );
                            const meta = riderMetaMap.get(Number(item.rider_id));
                            return {
                                ...item,
                                amount: monthRider?.rider_rate ?? "", // ✅ FIX HERE
                                name: meta?.rider_name ?? item.name,
                                acronym: meta?.acronym ?? item.acronym,
                            };
                        }),
                    };
                }
            );
            dispatch(setRateGroups(rates));
            
            // Create payment term labels for 66-80
            const ratesFor6680: RatesInfo[] = Array.from(
                { length: numberOfPaymentTerm },
                (_, index) => {
                    const month = index + 1;
                    return {
                        type: 'rider',
                        rider_id: month,
                        plan_id: null,
                        name: `${month} month${month > 1 ? "s" : ""}`,
                        amount: '', 
                        acronym: "",
                    };
                }
            );
            // Maximum supported payment terms is 12
            const cappedRates: RatesInfo[] = ratesFor6680.slice(0, 12);
            // Get API data for age 66-80
            const rates6670Raw = proposal.rates?.["66-70"] as Rate6670 | undefined;
            const rates7175Raw = proposal.rates?.["71-75"] as Rate7175 | undefined;
            const rates7680Raw = proposal.rates?.["76-80"] as Rate7680 | undefined;
            // Populate age 66-70
            if(isGCLI && proposal.borrower_age_66_70) {
                dispatch(resetRatesBasedOnRatesType({ type: '6670' }));
                dispatch(setRateForAll({ data: cappedRates, typeRate: '6670' }));
                dispatch(setGCLIRates6680FromApi({ rateType: 'gcliRates6670', apiData: rates6670Raw }));
            }
            // Populate age 71-75
            if(isGCLI && proposal.borrower_age_71_75) {
                dispatch(resetRatesBasedOnRatesType({ type: '7175' }));
                dispatch(setRateForAll({ data: cappedRates, typeRate: '7175' }));
                dispatch(setGCLIRates6680FromApi({ rateType: 'gcliRates7175', apiData: rates7175Raw }));
            }
            // Populate age 76-80
            if(isGCLI && proposal.borrower_age_76_80) {
                dispatch(resetRatesBasedOnRatesType({ type: '7680' }));
                dispatch(setRateForAll({ data: cappedRates, typeRate: '7680' }));
                dispatch(setGCLIRates6680FromApi({ rateType: 'gcliRates7680', apiData: rates7680Raw }));
            }
        }

        if (isGYRTOrIsGPAOrIsGCI) {
            const isBorrowerUnderMaxIsSelected = proposal.borrower_under_max ?? false
            // Check if proposal has 31 or more lives
            const isHighLives = numberOfLives >= RATES_CONSTANTS.maxNumberOfLive;
            // Get configured age range
            const minimumAge = proposal?.minimum_age;
            const maximumAge = proposal?.maximum_age;
            const isSameValueMinAge = minimumAge === RATES_CONSTANTS.minAgeProfile
            const isSameValueMaxAge = maximumAge === RATES_CONSTANTS.maxAgeProfile

            if (!minimumAge || !maximumAge) return;
            // Build age groups based on number of lives
            const baseGroup = defaultRateOf1865Dynamic.find(group => group.title === "18 to 65")!;

            // Build age groups based on number of lives
            const groups: RateGroup[] = isHighLives ? 
                (
                    isSameValueMinAge && isSameValueMaxAge
                        ? [
                            { 
                                ...baseGroup, 
                                title: "18 to 65", 
                            },
                        ]
                        : [ 
                            { 
                                ...baseGroup, 
                                title: `${minimumAge} to ${maximumAge}`,
                            },
                            // ...(isBorrowerUnderMaxIsSelected && 
                            ...(maximumAge < RATES_CONSTANTS.maxAgeProfile 
                                    ? [ 
                                        { 
                                            ...baseGroup, 
                                            title: `${maximumAge + 1} to ${RATES_CONSTANTS.maxAgeProfile}`
                                        }
                                    ] : 
                                    []
                            ),
                        ]
                ) : defaultRateOf1865Dynamic.filter(group => group.title !== "18 to 65");
            // Populate rates for each age group
            const sampleRates: RateGroup[] = groups.map((group) => {
                // Convert "30 to 55" -> "30-55"
                const rateKey = group.title.replace(" to ", "-") as AgeRangeKey;
                let planRate: AgeRate | undefined;
                if (isHighLives) {
                    // Use dynamic API response (e.g. 30-55, 56-65)
                    const currentRate = proposal?.rates?.[rateKey] as DynamicRate | undefined;
                    planRate = currentRate?.rates?.[0];
                } else {
                    // Use fixed 18-65 age buckets (age_18_24, age_25_29, etc.)
                    const currentRate = proposal?.rates?.["18-65"] as Rate1865 | undefined;
                    const ageKey = getAgeKey(group.title) as Rate1865AgeKey;
                    planRate = currentRate?.[ageKey]?.[0];
                }
                // Create lookup of rider_id -> rider_rate
                const riderRateMap = new Map<number, string>(
                    (planRate?.riders ?? []).map((r: RiderRate) => [
                        r.rider_id,
                        r.rider_rate ?? "",
                    ])
                );
                
                return {
                    title: group.title,
                    data: [
                        // Basic Plan Rates
                        {
                            type: "plan", 
                            rider_id: null,
                            plan_id: basePlanId,
                            name: basePlan,
                            amount: String(planRate?.basic_rate ?? ""),
                            acronym: basePlanAcronym,
                        },
                        // Rider Rates
                        ...(proposal.riders?.map((rider) => ({
                            type: "rider" as const,
                            plan_id: null,
                            rider_id: rider.rider_id,
                            name: rider.rider_name,
                            amount: String(riderRateMap.get(Number(rider.rider_id)) ?? "" ),
                            acronym: rider.acronym ?? "",
                        })) ?? []),
                    ],
                };
            });
            dispatch(setRateGroups(sampleRates));

            // Get payment-term rates for 66-80 age brackets
            const basicPlanGroup = proposal?.rates?.["18-65"] as Rate1865 | undefined;
            // Create lookup of term_of_months -> basic_rate
            const rateMap = new Map<number, string>(
                (basicPlanGroup?.basic_plan ?? []).map((rate: AgeRate) => [
                    rate.term_of_months ?? 0,
                    rate.basic_rate ?? "",
                ])
            );
            // Base plan template
            const basePlanItem: RatesInfo = {
                type: 'plan',
                rider_id: null,
                plan_id: basePlanId,
                name: basePlan,
                amount: '',
                acronym: basePlanAcronym ?? "",
            }

            // Create rider list for 66-80 rates
            const mappedRates: RatesInfo[] = [
                basePlanItem,
                ...(proposal.riders?.map((rider) => ({
                    plan_id: null,
                    rider_id: rider.rider_id,
                    name: rider.rider_name,
                    amount: rateMap.get(rider.rider_id)?.toString() || "",
                    acronym: rider.acronym || "",
                    type: "rider" as const,
                })) ?? []),
            ];
            // Keep only riders supported for 66-80
            const allowedIds: number[] = Object.values(PROPOSAL_RIDERS.ALCR.productFor); // add life
            const filteredRates = mappedRates.filter(rate => allowedIds.includes(rate.rider_id ?? 0));
            const ratesAndBasePlan: RatesInfo[] = [
                basePlanItem,
                ...filteredRates,
            ];
            // API data for 66-80
            const rates6670Raw = proposal.rates?.["66-70"] ?? {};
            const rates7175Raw = proposal.rates?.["71-75"] ?? {};
            const rates7680Raw = proposal.rates?.["76-80"] ?? {};
            // Populate 66-70
            if(isGYRT && proposal.borrower_age_66_70) {
                dispatch(resetRatesBasedOnRatesType({ type: '6670' }));
                dispatch(setRateForAll({ data: ratesAndBasePlan, typeRate: '6670' }));
                dispatch(setGYRTAndGPARates6680FromApi({ rateType: 'gcliRates6670', apiData: rates6670Raw }));
            }
            // Populate 71-75
            if(isGYRT && proposal.borrower_age_71_75) {
                dispatch(resetRatesBasedOnRatesType({ type: '7175' }));
                dispatch(setRateForAll({ data: ratesAndBasePlan, typeRate: '7175' }));
                dispatch(setGYRTAndGPARates6680FromApi({ rateType: 'gcliRates7175', apiData: rates7175Raw }));
            }
            // Populate 76-80
            if(isGYRT && proposal.borrower_age_76_80) {
                dispatch(resetRatesBasedOnRatesType({ type: '7680' }));
                dispatch(setRateForAll({ data: ratesAndBasePlan, typeRate: '7680' }));
                dispatch(setGYRTAndGPARates6680FromApi({ rateType: 'gcliRates7680', apiData: rates7680Raw }));
            }
        }

        dispatch(setShowRates({
            isShow6670: proposal.borrower_age_66_70,
            isShow7175: proposal.borrower_age_71_75,
            isShow7680: proposal.borrower_age_76_80,
        }))
    }, [dispatch, proposal, getAgeKey]);

    const populateEvidenceNotes = useCallback(() => {
        if(!proposal) return;
        dispatch(setEvidenceNotes(proposal?.evidence_notes ?? ''));
    }, [dispatch, proposal]);

    const populateTotalAmount = useCallback(() => {
        if(!proposal) return;

        dispatch(setAnnualPremiumAmount(proposal.total_annual_premium ?? null));
    }, [dispatch, proposal]);

    const populateProposalActuarialFiles = useCallback(() => {
        if(!proposal) return;
        dispatch(clearFilesDocuments())
        dispatch(populateFilesDocuments(
            proposal.actuarial_files?.map(item => ({
                fileName: item.file_name,
                filePath: item.file_path,
            })) ?? []
        ));
    }, [dispatch, proposal]);

    const populateProposalNotesForPDF = useCallback(() => {
        if(!proposal) return;
        dispatch(setNotes({ type: 'pdf', value: proposal.actuarial_notes }))
        dispatch(setNotes({ type: 'cfe', value: proposal.actuarial_to_cfe_notes }))
        dispatch(setIsShowToPDF(proposal.actuarial_notes_show_in_pdf))
        dispatch(setNoteForProposalAct({
            isShow: false,
            notesProposal: "",
            showCKEdit: false,
            copyNotes: "",
        }));
        dispatch(setIsEditingNotesForProposal(false))
    }, [dispatch, proposal]);

    const populateProposalNotesForCFE = useCallback(() => {
        if(!proposal) return;
        // dispatch(setNotes({ type: 'pdf', value: proposal.actuarial_notes }))
        dispatch(setNotes({ type: 'cfe', value: proposal.actuarial_to_cfe_notes }))
        // dispatch(setIsShowToPDF(proposal.actuarial_notes_show_in_pdf))
    }, [dispatch, proposal]);

    return {
        populateRates,
        populateEvidenceNotes,
        populateTotalAmount,
        populateProposalActuarialFiles,
        populateProposalNotesForPDF,
        populateProposalNotesForCFE
    }
}